import {
  homeKeywords,
  nice06Keywords,
  varToulonKeywords,
  tarifsKeywords,
  devisKeywords,
} from './topKeywords';

export type BlogPost = {
  slug: string;
  title: string;
  h1: string;
  subheading: string;
  description: string;
  image: string;
  imageAlt: string;
  category: string;
  city?: string;
  keywords: string[];
  readingTime: string;
  publishedAt: string;
  body: string;
};

export const blogPosts: BlogPost[] = [
  {
    slug: 'nettoyage-logement-insalubre-guide-06-83',
    title:
      'Nettoyage logement insalubre : guide complet Alpes Maritimes 06 et Var 83',
    h1: "Nettoyage logement insalubre : étapes d'une intervention professionnelle dans les Alpes Maritimes et le Var",
    subheading:
      'Comprendre les étapes clés d’un nettoyage logement insalubre 06 / 83',
    description:
      "Comment se déroule un nettoyage de logement insalubre ou très sale dans les Alpes Maritimes (06) et le Var (83) : étapes, délais et organisation d'une intervention professionnelle.",
    image: '/images/hero-nettoyage-logement-insalubre-06-var-avant-apres.png',
    imageAlt:
      'Avant après nettoyage logement insalubre appartement très sale Alpes Maritimes Var',
    category: 'Guide pratique',
    keywords: [
      homeKeywords[0],
      homeKeywords[1],
      'habitation insalubre',
      'nettoyage habitation insalubre',
    ],
    readingTime: '6 min',
    publishedAt: '2025-12-13',
    body: [
      '## Quand parle-t-on de logement insalubre ?',
      "Un nettoyage logement insalubre n’est jamais un simple « grand ménage ». Dans les Alpes Maritimes (06) et le Var (83), il s’agit souvent de remettre en état des logements très dégradés après des années d’accumulation de déchets, d’humidité, de nuisibles ou de manque total d’entretien.",
      "Les signes les plus fréquents : mauvaises odeurs persistantes, amas de sacs-poubelle, pièces inutilisables, traces d’urine ou d’excréments d’animaux, moisissures importantes, présence de cafards ou de rongeurs. Dans ces conditions, le logement devient dangereux pour la santé et l’intervention de spécialistes s’impose.",
      '## Étape 1 : diagnostic et devis',
      "Avant toute intervention, l’entreprise spécialisée réalise un diagnostic complet : état des sols et des murs, volume de déchets, présence éventuelle de nuisibles, risques électriques ou sanitaires. Ce repérage permet d’estimer la durée de l’intervention, le nombre de techniciens nécessaires et le niveau de protection à prévoir.",
      "À partir de ce diagnostic, un devis de nettoyage logement insalubre 06 / 83 est établi avec :",
      '- la description des travaux (débarras, nettoyage extrême, désinfection, désodorisation) ;',
      '- le nombre de jours d’intervention ;',
      '- le nombre de bennes ou de rotations nécessaires ;',
      '- le tarif global TTC.',
      '## Étape 2 : débarras et désencombrement',
      "La deuxième étape consiste à désencombrer le logement : tri, débarras et évacuation des déchets vers des déchetteries agréées. Dans certains cas extrêmes, plusieurs tonnes de déchets doivent être évacuées avant de pouvoir commencer le nettoyage en profondeur.",
      "Pendant cette phase, les équipes protègent les parties communes de l’immeuble, organisent un chemin de circulation et sécurisent les accès pour limiter les nuisances pour le voisinage.",
      '## Étape 3 : nettoyage extrême et désinfection',
      "Une fois le logement vidé, l’équipe passe au nettoyage extrême : dégraissage des surfaces, lessivage des murs, nettoyage des sols, remise en état de la cuisine et de la salle de bain, désinfection et traitement des mauvaises odeurs. Selon la situation, un traitement spécifique contre les moisissures ou certains nuisibles peut être recommandé.",
      '## Étape 4 : finitions et remise des lieux',
      "En fin de chantier, un contrôle complet est réalisé pièce par pièce. Les techniciens vérifient l’absence de résidus, l’efficacité de la désinfection et la bonne aération des lieux. Des photos avant / après peuvent être fournies pour les assurances, notaires ou agences immobilières.",
      "En confiant votre chantier à une entreprise spécialisée en nettoyage logement insalubre 06 / 83, vous bénéficiez d’un accompagnement global : organisation du débarras, nettoyage extrême, désinfection, désodorisation et, si besoin, production de justificatifs pour vos démarches administratives.",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-logement-insalubre-nice',
    title:
      'Nettoyage logement insalubre Nice : étapes, délais et prix indicatifs',
    h1: 'Nettoyage logement insalubre à Nice : comment se déroule une intervention ?',
    subheading:
      'Les spécificités du nettoyage logement insalubre à Nice (06)',
    description:
      "Focus sur le nettoyage de logements insalubres à Nice : déroulé d'une intervention, délais moyens et prix indicatifs pour les appartements et maisons niçois.",
    image: '/images/cover-nettoyage-logement-insalubre-alpes-maritimes-var.png',
    imageAlt:
      'Nettoyage logement insalubre Nice avec débarras et désinfection',
    category: 'Nice 06',
    city: 'Nice',
    keywords: [
      nice06Keywords[0],
      nice06Keywords[2],
      'nettoyage appartement insalubre nice',
    ],
    readingTime: '5 min',
    publishedAt: '2025-12-13',
    body: [
      '## Situations fréquentes de logements insalubres à Nice',
      "À Nice, les situations de logements insalubres concernent aussi bien des studios étudiants en centre-ville que des appartements familiaux ou des logements sociaux. Accumulation de déchets, odeurs fortes, humidité et nuisibles rendent le logement inhabitable et nécessitent une intervention professionnelle.",
      '## Comment se déroule une intervention de nettoyage logement insalubre Nice ?',
      "Une intervention de nettoyage logement insalubre Nice commence par une analyse détaillée de la situation : type de logement, surface, volume de déchets, accessibilité de l’immeuble, voisinage. Cette étape permet d’établir un devis réaliste et un planning d’intervention clair.",
      "Le jour de l’intervention, l’équipe commence par le débarras : tri, démontage éventuel de meubles, mise en sacs, évacuation vers la déchetterie. Une fois l’appartement désencombré, le nettoyage extrême peut commencer : décapage des sols, dégraissage en profondeur de la cuisine, désinfection de la salle de bain et des sanitaires, traitement des odeurs et aération prolongée.",
      '## Prix d’un nettoyage logement insalubre à Nice',
      "Les prix d’un nettoyage logement insalubre à Nice varient en fonction de plusieurs critères :",
      '- la surface du logement (studio, T2, T3, maison) ;',
      "- le niveau d’insalubrité (logement très sale, Diogène, squat, longue vacance) ;",
      "- le volume de déchets et d’encombrants à évacuer ;",
      "- l’accessibilité (étage élevé sans ascenseur, rues étroites, stationnement délicat).",
      "Pour obtenir une estimation fiable, il est indispensable de fournir des photos ou de prévoir une visite sur place afin d’évaluer précisément la situation.",
      '## Pourquoi faire appel à une entreprise spécialisée à Nice ?',
      "Que vous soyez propriétaire, locataire, agence ou syndic, faire appel à une entreprise spécialisée en nettoyage logement insalubre Nice vous permet de gagner du temps, de limiter le stress et de remettre rapidement le bien sur le marché ou à la location, dans des conditions d’hygiène et de sécurité satisfaisantes.",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-maison-insalubre-var-toulon',
    title: 'Nettoyage maison insalubre Var Toulon : que faut-il prévoir ?',
    h1: 'Nettoyage de maison insalubre dans le Var : focus sur Toulon et le littoral',
    subheading:
      'Organiser un nettoyage maison insalubre Var Toulon de A à Z',
    description:
      'Conseils pour organiser un nettoyage de maison insalubre dans le Var, notamment à Toulon et sur le littoral : étapes clés, durée d’intervention et budget.',
    image: '/images/hero-nettoyage-logement-insalubre-06-var-avant-apres.png',
    imageAlt:
      'Maison insalubre avant après nettoyage extrême dans le Var à Toulon',
    category: 'Var 83',
    city: 'Toulon',
    keywords: [
      varToulonKeywords[0],
      varToulonKeywords[1],
      'nettoyage maison tres sale var',
    ],
    readingTime: '5 min',
    publishedAt: '2025-12-13',
    body: [
      '## Quand demander un nettoyage maison insalubre Var Toulon ?',
      "Dans le Var, et particulièrement à Toulon et sur le littoral, les maisons insalubres sont souvent découvertes à la suite d’un départ de locataire, d’une succession ou d’un syndrome de Diogène. Avant toute vente ou relocation, un nettoyage maison insalubre Var est indispensable pour remettre le bien à niveau.",
      '## Point de départ : état des lieux et photos',
      "La première étape consiste à faire un point complet sur la situation : surface de la maison, nombre de pièces, présence de cave ou de garage, volume d’encombrants, état des sanitaires et de la cuisine. Ces éléments permettent d’orienter l’entreprise de nettoyage maison insalubre Var vers la solution la plus adaptée.",
      "Des photos claires de chaque pièce (cuisine, salle de bain, pièces de vie, circulation, annexes) aident à établir un devis réaliste sans forcément se déplacer dans un premier temps.",
      '## Déroulement du nettoyage maison insalubre à Toulon',
      "Une fois le devis validé, les équipes interviennent généralement en deux phases :",
      "- le débarras : démontage de meubles, tri, évacuation des déchets vers des filières agréées ;",
      "- le nettoyage extrême : lessivage des murs, nettoyage des sols, dégraissage de la cuisine, désinfection des salles d’eau, traitement des odeurs et aération.",
      "Selon la configuration (maison de plain-pied, villa à étages, jardin encombré…), l’intervention peut durer d’une journée à plusieurs jours.",
      '## Quel budget prévoir pour une maison insalubre dans le Var ?',
      "Les prix d’un nettoyage maison insalubre Var Toulon dépendent de la surface, du niveau d’encrassement, de l’accessibilité et de la localisation précise. Pour un pavillon très encombré, le budget peut être significatif mais permet de sauver un bien qui serait autrement difficilement vendable ou relouable.",
      "Faire appel à une entreprise spécialisée en nettoyage maison insalubre Var, c’est gagner du temps, sécuriser le chantier et garantir un résultat professionnel, conforme aux attentes des acheteurs, locataires ou de la famille.",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-logement-insalubre-cannes',
    title:
      'Nettoyage logement insalubre Cannes : intervention rapide et discrète',
    h1: 'Nettoyage logement insalubre à Cannes : comment se déroule une intervention ?',
    subheading:
      "Zoom sur le nettoyage logement insalubre à Cannes et dans l'ouest des Alpes Maritimes",
    description:
      'Exemples concrets d’intervention pour un nettoyage de logement insalubre à Cannes et ses environs : étapes, délais moyens et budget.',
    image: '/images/cover-nettoyage-logement-insalubre-alpes-maritimes-var.png',
    imageAlt:
      'Nettoyage logement insalubre à Cannes avec débarras et désinfection',
    category: 'Alpes Maritimes 06',
    city: 'Cannes',
    keywords: [
      nice06Keywords[1],
      nice06Keywords[2],
      'nettoyage logement insalubre cannes',
    ],
    readingTime: '4 min',
    publishedAt: '2025-12-14',
    body: [
      '## Logements insalubres à Cannes : quels profils ?',
      "À Cannes, les demandes de nettoyage logement insalubre concernent aussi bien des appartements en résidence que des maisons individuelles ou des studios proches de la Croisette. Dans tous les cas, il s’agit de remettre le logement à niveau pour pouvoir le relouer, le revendre ou le récupérer pour la famille.",
      '## Analyse de la situation et devis',
      "Avant d’intervenir, l’équipe effectue un diagnostic simple : surface du logement, volume d’encombrants, accès (étage, ascenseur, cour intérieure), présence éventuelle de nuisibles ou de fortes odeurs. Ces éléments permettent de proposer un devis nettoyage logement insalubre Cannes cohérent avec la réalité du chantier.",
      '## Déroulement type d’un nettoyage logement insalubre Cannes',
      "Le jour J, l’intervention commence par le débarras : tri, mise en sacs, démontage des meubles inutilisables et évacuation vers la déchetterie. Une fois le logement désencombré, le nettoyage extrême peut débuter : dégraissage puissant, lessivage des murs, nettoyage des sols, remise en état de la cuisine et de la salle de bain.",
      "Une entreprise spécialisée en nettoyage logement insalubre à Cannes dispose du matériel, des protections et de l’expérience nécessaires pour intervenir rapidement et en toute discrétion, en limitant au maximum les nuisances pour le voisinage.",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-logement-insalubre-toulon',
    title:
      'Nettoyage logement insalubre Toulon : déroulement et prix indicatifs',
    h1: 'Nettoyage logement insalubre à Toulon : étapes clés et budget moyen',
    subheading:
      'Comment se passe un nettoyage logement insalubre à Toulon et dans le Var 83 ?',
    description:
      "Détail des différentes étapes d'un nettoyage logement insalubre à Toulon, des délais d’intervention et des fourchettes de prix les plus fréquentes.",
    image: '/images/hero-nettoyage-logement-insalubre-06-var-avant-apres.png',
    imageAlt:
      'Avant après nettoyage logement insalubre à Toulon dans le Var',
    category: 'Var 83',
    city: 'Toulon',
    keywords: [
      varToulonKeywords[1],
      varToulonKeywords[2],
      'nettoyage logement insalubre toulon',
    ],
    readingTime: '5 min',
    publishedAt: '2025-12-14',
    body: [
      '## Pourquoi un nettoyage logement insalubre à Toulon ?',
      "À Toulon, de nombreux logements deviennent insalubres après un départ de locataire, un squat, un cas de syndrome de Diogène ou plusieurs années sans entretien. Avant toute remise en location ou mise en vente, un nettoyage logement insalubre Var Toulon s’impose.",
      '## Évaluation de la situation et préparation du devis',
      "L’intervention commence par une phase de repérage : surface de l’appartement ou de la maison, nombre de pièces, volume de déchets, accessibilité pour les bennes et les véhicules utilitaires. Sur cette base, un devis nettoyage logement insalubre Toulon peut être établi avec une estimation des délais.",
      '## Débarras et désencombrement',
      "Le débarras est ensuite réalisé de manière méthodique : vidage des placards, tri des objets, démontage des meubles inutilisables, évacuation vers des filières agréées. Cette phase permet de rendre le logement à nouveau circulable et de préparer le terrain pour le nettoyage extrême.",
      '## Nettoyage extrême et traitement des odeurs',
      "Le nettoyage proprement dit comprend un dégraissage intensif, la désinfection des surfaces, le traitement des odeurs, un aèrage prolongé et, si nécessaire, un traitement spécifique contre les nuisibles ou certaines moisissures.",
      "En faisant appel à une entreprise spécialisée en nettoyage logement insalubre à Toulon, vous sécurisez le chantier, gagnez du temps et obtenez un résultat professionnel qui facilitera la suite du projet (revente, relocation, succession).",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-syndrome-de-diogene',
    title:
      'Nettoyage syndrome de Diogène : déroulement d’une intervention extrême',
    h1: 'Nettoyage syndrome de Diogène : comment gérer un logement extrêmement encombré ?',
    subheading:
      'Les spécificités et étapes d’un nettoyage après syndrome de Diogène',
    description:
      'Le syndrome de Diogène entraîne souvent des logements extrêmement encombrés et insalubres. Découvrez comment se déroule un nettoyage professionnel dans ce contexte.',
    image: '/images/gallery-diogene-nice-avant-apres.png',
    imageAlt:
      'Avant après nettoyage logement syndrome de Diogène à Nice avec gros débarras',
    category: 'Syndrome de Diogène',
    keywords: [
      'nettoyage syndrome de diogene',
      'nettoyage diogene',
      'nettoyage habitation insalubre',
    ],
    readingTime: '7 min',
    publishedAt: '2025-12-13',
    body: [
      '## Qu’est-ce que le syndrome de Diogène ?',
      "Le syndrome de Diogène se manifeste par une accumulation extrême d’objets et de déchets, associée à une négligence sévère de l’hygiène. Les logements concernés sont souvent méconnaissables, avec des piles de sacs, de journaux, de vêtements et de détritus dans chaque pièce.",
      '## Pourquoi un nettoyage spécialisé est indispensable ?',
      "Un nettoyage syndrome de Diogène ne s’improvise pas. L’intervention débute par une analyse des risques : stabilité des sols, présence de nuisibles, risques biologiques, objets coupants ou piquants, atmosphère chargée. Des équipements de protection individuelle (combinaisons, gants, masques, lunettes) sont indispensables pour intervenir en sécurité.",
      '## Déroulement d’un nettoyage syndrome de Diogène',
      "Le débarras se fait progressivement, pièce par pièce, en triant ce qui peut éventuellement être conservé et en évacuant le reste. Le volume de déchets peut être très important et nécessiter plusieurs bennes. Une fois les pièces vidées, le nettoyage extrême peut commencer : décapage, désinfection, désodorisation et aération sur plusieurs heures.",
      '## Accompagnement humain et discrétion',
      "En complément du nettoyage, l’accompagnement humain est crucial. Une entreprise spécialisée en nettoyage syndrome de Diogène sait intervenir avec tact et discrétion auprès de la personne concernée et de son entourage, en respectant l’histoire du lieu tout en le remettant en état.",
    ].join('\n\n'),
  },
  {
    slug: 'nettoyage-extreme-tarifs',
    title:
      'Nettoyage extrême : dans quels cas y recourir et à quels tarifs ?',
    h1: 'Nettoyage extrême : exemples de situations et fourchettes de prix en 06 / 83',
    subheading:
      'Quand faire appel à un nettoyage extrême et comment se calcule le prix ?',
    description:
      'Qu’est-ce qu’un nettoyage extrême et dans quels cas y recourir ? Zoom sur les tarifs et les facteurs qui les font varier dans les Alpes Maritimes et le Var.',
    image: '/images/hero-nettoyage-logement-insalubre-06-var-avant-apres.png',
    imageAlt:
      'Exemple de nettoyage extrême avant après appartement très sale',
    category: 'Tarifs',
    keywords: [
      'nettoyage extreme',
      'nettoyage extreme prix',
      'entreprise nettoyage extreme',
    ],
    readingTime: '5 min',
    publishedAt: '2025-12-13',
    body: [
      '## Qu’appelle-t-on nettoyage extrême ?',
      "On parle de nettoyage extrême lorsque l’état du logement dépasse largement un simple manque d’entretien : accumulation massive de déchets, odeurs très fortes, traces de fluides, présence de nuisibles, risques sanitaires importants. Ces chantiers demandent une organisation et des protections spécifiques.",
      '## Exemples de situations nécessitant un nettoyage extrême',
      "Les situations typiques de nettoyage extrême sont les logements touchés par le syndrome de Diogène, certains squats, des appartements laissés à l’abandon après un départ précipité ou des sinistres. Chaque cas est unique et doit être étudié avant d’annoncer un tarif.",
      '## Les critères qui font varier le prix',
      "Les principaux facteurs qui influencent le prix d’un nettoyage extrême sont :",
      '- la surface du logement ;',
      "- le volume d’encombrants à évacuer ;",
      "- le niveau de contamination (fluides, nuisibles, moisissures) ;",
      "- l’accessibilité (étage, ascenseur, accès camion, stationnement).",
      '## Fourchettes de prix constatées en 06 / 83',
      "Dans les Alpes Maritimes et le Var, les budgets constatés vont de quelques centaines d’euros pour un petit logement très sale à plusieurs milliers d’euros pour une grande maison extrêmement dégradée. Seul un devis détaillé, basé sur des photos ou une visite, permet d’obtenir un prix nettoyage extrême vraiment adapté à la situation.",
    ].join('\n\n'),
  },
  {
    slug: 'tarif-nettoyage-logement-insalubre',
    title:
      'Tarif nettoyage logement insalubre : comment se calcule le prix final ?',
    h1: 'Tarif nettoyage logement insalubre : les principaux critères de prix',
    subheading:
      'Surface, volume de déchets, risques : ce qui fait varier le tarif nettoyage logement insalubre',
    description:
      "Découvrez les critères qui influencent le tarif d'un nettoyage de logement insalubre et les fourchettes de prix généralement constatées.",
    image: '/images/tarifs-nettoyage-logement-insalubre-06-83.png',
    imageAlt:
      "Illustration des tarifs de nettoyage logement insalubre sur la Côte d'Azur",
    category: 'Tarifs',
    keywords: [
      tarifsKeywords[0],
      tarifsKeywords[1],
      'prix nettoyage logement insalubre',
    ],
    readingTime: '4 min',
    publishedAt: '2025-12-13',
    body: [
      '## Les critères qui impactent le tarif',
      "Le tarif d'un nettoyage logement insalubre dépend de plusieurs critères : la surface du bien, le volume de déchets, le niveau de saleté, la présence éventuelle de nuisibles, ainsi que la localisation et l'accessibilité du logement.",
      'Les principaux critères à prendre en compte sont :',
      '- la surface (studio, T2, T3, maison) ;',
      "- le niveau d’insalubrité (simple logement très sale, cas de Diogène, logement abandonné) ;",
      '- le nombre de bennes à prévoir ;',
      "- les contraintes d’accès (étage sans ascenseur, centre-ville, parking difficile).",
      '## Fourchettes de prix les plus fréquentes',
      'À titre indicatif, les fourchettes de prix les plus fréquentes pour un nettoyage logement insalubre 06 / 83 sont :',
      '- Studio / petit logement très sale (20–30 m²) : 800 € à 1 500 € TTC ;',
      '- Appartement T2 / T3 insalubre (40–70 m²) : 1 500 € à 3 000 € TTC ;',
      '- Grande maison insalubre très dégradée (90–150 m² et plus) : 3 000 € à 6 000 € TTC et plus dans les cas extrêmes.',
      '## Pourquoi demander un devis personnalisé ?',
      "Ces montants restent des moyennes observées sur des chantiers réels. Seul un devis personnalisé, basé sur votre situation (photos, description précise, localisation), permet d’obtenir un tarif nettoyage logement insalubre fiable et adapté.",
    ].join('\n\n'),
  },
  {
    slug: 'devis-nettoyage-logement-insalubre',
    title:
      'Devis nettoyage logement insalubre : quelles informations transmettre ?',
    h1: 'Devis nettoyage logement insalubre : les informations indispensables',
    subheading:
      'Quelles informations fournir pour un devis nettoyage logement insalubre précis ?',
    description:
      "Pour obtenir un devis précis pour le nettoyage d'un logement insalubre, certaines informations sont indispensables. Liste de ce qu'il faut préparer.",
    image: '/images/tarifs-nettoyage-logement-insalubre-06-83.png',
    imageAlt:
      'Exemple de devis pour nettoyage logement insalubre avec estimation de prix',
    category: 'Devis',
    keywords: [
      devisKeywords[0],
      devisKeywords[1],
      'entreprise nettoyage logement insalubre',
    ],
    readingTime: '3 min',
    publishedAt: '2025-12-13',
    body: [
      '## Les informations de base à transmettre',
      "Un devis nettoyage logement insalubre sérieux ne peut pas se faire « au hasard ». Pour que l’estimation soit fiable, l’entreprise doit comprendre l’ampleur du chantier : type de logement, surface, volume de déchets, niveau d’insalubrité, ville d’intervention.",
      "Pour préparer votre demande de devis, commencez par indiquer : la surface approximative, le type de logement (studio, T2, maison…), la ville et le département (Nice, Cannes, Antibes, Toulon, Fréjus, Alpes Maritimes 06, Var 83…).",
      '## Décrire la situation le plus précisément possible',
      "Ajoutez ensuite une description simple mais précise : logement très sale, logement insalubre, cas de syndrome de Diogène, squat, présence de nuisibles, odeurs fortes, durée d’occupation. Plus ces informations sont détaillées, plus l’entreprise pourra affiner le tarif.",
      '## L’importance des photos pour le devis',
      "Les photos sont essentielles pour un devis nettoyage logement insalubre précis : cuisine, salle de bain, pièces principales, couloirs, cave ou garage éventuels. Elles permettent d’évaluer le volume de déchets, le niveau d’encrassement et les risques potentiels.",
      '## Un devis détaillé pour éviter les mauvaises surprises',
      "Plus votre demande est complète, plus le devis sera fiable et le planning d’intervention réaliste. Vous gagnez du temps, évitez les mauvaises surprises et obtenez un tarif nettoyage logement insalubre adapté à votre situation.",
    ].join('\n\n'),
  },
  {
    slug: 'debarras-logement-insalubre',
    title: 'Débarras logement insalubre : par où commencer et qui contacter ?',
    h1: 'Débarras logement insalubre : organisation, sécurité et évacuation des déchets',
    subheading:
      'Comment organiser un débarras de logement insalubre avant le nettoyage extrême ?',
    description:
      "Le débarras est souvent la première étape du nettoyage d'un logement insalubre. Découvrez comment l'organiser efficacement et en sécurité.",
    image: '/images/gallery-diogene-nice-avant-apres.png',
    imageAlt:
      'Débarras massif de logement insalubre avec sacs de déchets et bennes',
    category: 'Débarras',
    keywords: [
      'debarras logement insalubre',
      'nettoyage logement insalubre',
      'nettoyage lieux insalubres',
    ],
    readingTime: '4 min',
    publishedAt: '2025-12-13',
    body: [
      '## Pourquoi le débarras est une étape clé ?',
      "Le débarras d’un logement insalubre est une étape incontournable avant le nettoyage extrême. Accumulations de déchets, meubles abîmés, objets entassés : tant que le logement n’est pas désencombré, il est impossible de nettoyer correctement et de traiter les odeurs ou les nuisibles.",
      '## Organisation pratique du débarras',
      "Un débarras logement insalubre doit être organisé méthodiquement : repérage des pièces les plus encombrées, mise en place d’un chemin de circulation, protection des parties communes de l’immeuble, choix du nombre de bennes et de véhicules adaptés.",
      '## Pourquoi faire appel à des professionnels ?',
      "Dans les cas les plus lourds (syndrome de Diogène, squat, habitation abandonnée), il est fortement déconseillé d’intervenir seul. Une entreprise spécialisée en débarras logement insalubre dispose des équipements de protection (gants, masques, combinaisons), de l’expérience et des assurances nécessaires pour gérer ce type de chantier.",
      '## Et après le débarras ?',
      "Une fois le débarras terminé, le logement est prêt pour le nettoyage extrême : dégraissage, désinfection, désodorisation, traitement des surfaces. En combinant débarras et nettoyage logement insalubre, vous transformez un bien invivable en un logement à nouveau habitable, vendable ou relouable.",
    ].join('\n\n'),
  },
  {
    slug: 'prix-nettoyage-appartement-tres-sale',
    title:
      'Prix nettoyage appartement très sale : combien prévoir pour un logement insalubre ?',
    h1: 'Prix nettoyage appartement très sale ou insalubre en 06 / 83',
    subheading:
      'Fourchettes de prix et critères pour un nettoyage appartement très sale ou insalubre',
    description:
      "Quels sont les prix moyens pour un nettoyage d'appartement très sale ou insalubre dans les Alpes Maritimes et le Var, et quels critères font varier le budget ?",
    image: '/images/tarifs-nettoyage-logement-insalubre-06-83.png',
    imageAlt:
      'Illustration des prix de nettoyage appartement très sale et insalubre',
    category: 'Tarifs',
    keywords: [
      'prix nettoyage appartement',
      'prix nettoyage appartement tres sale',
      'nettoyage appartement insalubre',
    ],
    readingTime: '4 min',
    publishedAt: '2025-12-14',
    body: [
      '## Quels appartements sont concernés ?',
      "Le prix d’un nettoyage appartement très sale dépend d’abord de la situation : simple manque d’entretien, logement resté vide plusieurs mois, cas de syndrome de Diogène ou véritable appartement insalubre avec amas de déchets et fortes odeurs.",
      "Les demandes les plus fréquentes concernent des studios et T2 en centre-ville (Nice, Cannes, Toulon, Fréjus…) qui doivent être remis en état rapidement pour une nouvelle location ou une vente.",
      '## Les critères qui font varier le prix',
      "Plusieurs éléments font varier le prix d’un nettoyage appartement insalubre :",
      '- la surface (studio, T2, T3…) ;',
      "- le niveau de saleté et d’insalubrité ;",
      "- le volume d’encombrants à évacuer ;",
      "- l’accessibilité (étage sans ascenseur, rues étroites, stationnement difficile).",
      '## Fourchettes de prix constatées en 06 / 83',
      "À titre indicatif, pour un nettoyage appartement très sale ou insalubre dans les Alpes Maritimes (06) et le Var (83) :",
      '- petit studio très sale : à partir de 800 € TTC ;',
      '- appartement T2 / T3 insalubre : le plus souvent entre 1 500 € et 3 000 € TTC ;',
      "- grands appartements ou situations extrêmes : budget sur devis après analyse de la situation.",
      '## Pourquoi demander un devis personnalisé ?',
      "Chaque appartement est différent. Pour obtenir un prix nettoyage appartement très sale réaliste, il est indispensable de transmettre des photos et une description précise de la situation (surface, ville, niveau d’encombrement). L’entreprise pourra ainsi proposer un devis adapté et un planning d’intervention clair.",
    ].join('\n\n'),
  },
];
